import { Center, Spinner, Text } from "@chakra-ui/react";
import { GoogleMap } from "@react-google-maps/api";
import React from "react";
import { ClockLocation } from "../utils/location-parser";
import { useGoogleMapsLoader } from "../hooks/useGoogleMapsLoader";

interface ClockLocationModalContentProps {
  location: ClockLocation;
  patientId: number;
  patientAddressLocation: any; // Will be {lat: number, lng: number} | null after GraphQL types regenerate
}

export function ClockLocationModalContent({
  location,
  patientId: _patientId,
  patientAddressLocation,
}: ClockLocationModalContentProps) {
  const mapRef = React.useRef<google.maps.Map | null>(null);
  const clockMarkerRef = React.useRef<google.maps.Marker | null>(null);
  const patientMarkerRef = React.useRef<google.maps.Marker | null>(null);
  const geofenceCircleRef = React.useRef<google.maps.Circle | null>(null);
  const { isLoaded, loadError } = useGoogleMapsLoader();

  // Parse patient address location
  const patientLocation = React.useMemo(() => {
    if (!patientAddressLocation) return null;

    try {
      let parsed: any;
      if (typeof patientAddressLocation === "string") {
        parsed = JSON.parse(patientAddressLocation);
      } else {
        parsed = patientAddressLocation;
      }

      // Handle GraphQL's {v: ...} wrapper
      if (parsed.v && typeof parsed.v === "object") {
        parsed = parsed.v;
      }

      const lat = parsed.latitude ?? parsed.lat;
      const lng = parsed.longitude ?? parsed.lng;

      if (typeof lat === "number" && typeof lng === "number") {
        return { lat, lng };
      }
      return null;
    } catch (error) {
      console.error("[ClockLocationModal] Failed to parse patient address:", error);
      return null;
    }
  }, [patientAddressLocation]);

  const mapCenter = {
    lat: location.latitude,
    lng: location.longitude,
  };

  const handleLoad = (map: google.maps.Map) => {
    mapRef.current = map;

    // Create clock location marker (red pin)
    clockMarkerRef.current = new google.maps.Marker({
      position: mapCenter,
      map: map,
      title: "Clock Location",
    });

    // Create patient address marker and geofence if available
    if (patientLocation) {
      // Patient address marker (green circle)
      patientMarkerRef.current = new google.maps.Marker({
        position: patientLocation,
        map: map,
        title: "Patient Address",
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: "#22c55e",
          fillOpacity: 1,
          strokeColor: "#16a34a",
          strokeWeight: 2,
        },
      });

      // Geofence circle (300m radius)
      // Color: green if clock location is within bounds, red if out of bounds
      const circleColor = location.outOfBounds ? "#ef4444" : "#22c55e";
      geofenceCircleRef.current = new google.maps.Circle({
        map: map,
        center: patientLocation,
        radius: 300, // 300 meters
        strokeColor: circleColor,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: circleColor,
        fillOpacity: 0.15,
      });

      // Adjust map bounds to show both markers
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(mapCenter);
      bounds.extend(patientLocation);
      map.fitBounds(bounds);

      // Ensure minimum zoom level
      google.maps.event.addListenerOnce(map, "idle", () => {
        const currentZoom = map.getZoom();
        if (currentZoom && currentZoom > 15) {
          map.setZoom(15);
        }
      });
    }
  };

  const handleUnmount = () => {
    // Clean up clock marker
    if (clockMarkerRef.current) {
      clockMarkerRef.current.setMap(null);
      clockMarkerRef.current = null;
    }

    // Clean up patient marker
    if (patientMarkerRef.current) {
      patientMarkerRef.current.setMap(null);
      patientMarkerRef.current = null;
    }

    // Clean up geofence circle
    if (geofenceCircleRef.current) {
      geofenceCircleRef.current.setMap(null);
      geofenceCircleRef.current = null;
    }

    mapRef.current = null;
  };

  if (loadError) {
    return <Text>Map cannot be loaded right now.</Text>;
  }

  if (!isLoaded) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <GoogleMap
      center={mapCenter}
      mapContainerStyle={{ width: "100%", height: "400px" }}
      options={{
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
      }}
      zoom={15}
      onLoad={handleLoad}
      onUnmount={handleUnmount}
    />
  );
}
